'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { PublicSearch } from './PublicSearch';
import type { PublicSearchEntry } from '@/lib/marketing/search';
import {
  Activity,
  ArrowRight,
  BarChart3,
  BriefcaseBusiness,
  Building2,
  CalendarDays,
  CheckCircle2,
  ChevronDown,
  ClipboardCheck,
  Factory,
  FileText,
  FolderKanban,
  Layers3,
  Menu,
  Package,
  ShieldCheck,
  Sparkles,
  Target,
  UserPlus,
  Users,
  WalletCards,
  Wrench,
  X,
} from 'lucide-react';

type HeaderVariant = 'dark' | 'light';

type MenuItem = {
  title: string;
  description: string;
  href: string;
  icon: React.ComponentType<{ size?: number; strokeWidth?: number }>;
};

type MegaMenu = {
  label: string;
  href: string;
  featured: {
    eyebrow: string;
    title: string;
    description: string;
    href: string;
    icon: React.ComponentType<{ size?: number; strokeWidth?: number }>;
  };
  items: MenuItem[];
};

const platformMenu: MegaMenu = {
  label: 'Platform',
  href: '/platform',
  featured: {
    eyebrow: 'COMPLETE ONEHR PLATFORM',
    title: 'Everything your workforce needs.',
    description:
      'Connect employee management, workforce, payroll, performance and intelligence in one place.',
    href: '/platform',
    icon: Layers3,
  },
  items: [
    {
      title: 'Employee Management',
      description: 'Manage the complete employee lifecycle.',
      href: '/platform/employee-management',
      icon: Users,
    },
    {
      title: 'Workforce Management',
      description: 'Review assignments, shifts and attendance.',
      href: '/platform/workforce-management',
      icon: BriefcaseBusiness,
    },
    {
      title: 'Attendance',
      description: 'Track attendance, shifts and overtime.',
      href: '/platform/attendance',
      icon: ClipboardCheck,
    },
    {
      title: 'Leave Management',
      description: 'Simplify leave, balances and approvals.',
      href: '/platform/leave',
      icon: CalendarDays,
    },
    {
      title: 'Payroll',
      description: 'Review payroll and configured deductions.',
      href: '/platform/payroll',
      icon: WalletCards,
    },
    {
      title: 'Performance',
      description: 'Goals, reviews and performance insights.',
      href: '/platform/performance',
      icon: Target,
    },
    {
      title: 'Recruitment & Onboarding',
      description: 'Move candidates into your workforce.',
      href: '/platform/recruitment',
      icon: UserPlus,
    },
    {
      title: 'Documents',
      description: 'Keep employee documents organized.',
      href: '/platform/documents',
      icon: FileText,
    },
    {
      title: 'Analytics & Reports',
      description: 'See workforce data and management insights.',
      href: '/platform/analytics',
      icon: BarChart3,
    },
    {
      title: 'AI Assistant',
      description: 'Bring intelligent help into daily HR work.',
      href: '/platform/ai',
      icon: Sparkles,
    },
    {
      title: 'Organization Management',
      description: 'Manage departments, branches and teams.',
      href: '/platform/organization',
      icon: Building2,
    },
    {
      title: 'Projects',
      description: 'Assign tasks and review recorded progress.',
      href: '/platform/projects',
      icon: FolderKanban,
    },
    {
      title: 'Production',
      description: 'Supporting workflows; dedicated planning is planned.',
      href: '/platform/production',
      icon: Factory,
    },
    {
      title: 'Inventory',
      description: 'Products, warehouses and stock movements.',
      href: '/platform/inventory',
      icon: Package,
    },
  ],
};

const solutionsMenu: MegaMenu = {
  label: 'Solutions',
  href: '/solutions',
  featured: {
    eyebrow: 'WORKFORCE OPERATIONS',
    title: 'Connect people to the work.',
    description:
      'Explore available project, workforce and inventory workflows, with clear scope notes.',
    href: '/solutions',
    icon: Factory,
  },
  items: [
    {
      title: 'Project Planning',
      description: 'Plan projects, assign people and track delivery.',
      href: '/solutions/project-planning',
      icon: FolderKanban,
    },
    {
      title: 'Production Management',
      description: 'Factory workforce today; dedicated production planning is planned.',
      href: '/solutions/production-management',
      icon: Factory,
    },
    {
      title: 'Breakdown Management',
      description: 'Assigned-asset issues today; dedicated maintenance is planned.',
      href: '/solutions/breakdown-management',
      icon: Wrench,
    },
    {
      title: 'Inventory Management',
      description: 'Review stock records, warehouses and movements.',
      href: '/solutions/inventory-management',
      icon: Package,
    },
    {
      title: 'Workforce Operations',
      description: 'Unify people, schedules and operational workflows.',
      href: '/solutions/workforce-operations',
      icon: Layers3,
    },
    {
      title: 'Payroll & Compliance',
      description: 'Connect payroll inputs and statutory processes.',
      href: '/solutions/payroll-compliance',
      icon: ShieldCheck,
    },
    {
      title: 'Performance Management',
      description: 'Goals, review cycles and employee/manager input.',
      href: '/solutions/performance-management',
      icon: Target,
    },
  ],
};

const industriesMenu: MegaMenu = {
  label: 'Industries',
  href: '/industries',
  featured: {
    eyebrow: 'BUILT FOR YOUR INDUSTRY',
    title: 'Built for how your business works.',
    description: 'Adapt HR and workforce operations to the way your industry actually runs.',
    href: '/industries',
    icon: Building2,
  },
  items: [
    {
      title: 'Manufacturing',
      description: 'Factory employee, attendance and payroll workflows.',
      href: '/industries/manufacturing',
      icon: Factory,
    },
    {
      title: 'Garments & Textiles',
      description: 'Factory employee, shift and attendance workflows.',
      href: '/industries/garments',
      icon: Layers3,
    },
    {
      title: 'IT & Technology',
      description: 'Modern teams, projects and performance.',
      href: '/industries/technology',
      icon: Activity,
    },
    {
      title: 'Construction',
      description: 'Site workforce, projects and assets.',
      href: '/industries/construction',
      icon: Wrench,
    },
    {
      title: 'Healthcare',
      description: 'People, shifts and workforce administration.',
      href: '/industries/healthcare',
      icon: CheckCircle2,
    },
    {
      title: 'Retail & Distribution',
      description: 'Distributed teams and inventory visibility.',
      href: '/industries/retail',
      icon: Package,
    },
    {
      title: 'Professional Services',
      description: 'People, projects and performance.',
      href: '/industries/services',
      icon: BriefcaseBusiness,
    },
  ],
};

const menus = [platformMenu, solutionsMenu, industriesMenu];

function Logo({ variant }: { variant: HeaderVariant }) {
  return (
    <Link href="/" aria-label="Mentrox OneHR home" className="flex shrink-0 items-center">
      <div className="relative h-10 w-[142px] sm:h-11 sm:w-[155px]">
        <Image
          src="/assets/images/app_logo.webp"
          alt="Mentrox OneHR"
          fill
          sizes="(min-width: 640px) 155px, 142px"
          priority
          className={[
            'h-full w-full object-contain object-left',
            variant === 'dark' ? 'brightness-110' : '',
          ].join(' ')}
        />
      </div>
    </Link>
  );
}

function MegaMenuDropdown({
  menu,
  variant,
  onNavigate,
}: {
  menu: MegaMenu;
  variant: HeaderVariant;
  onNavigate: () => void;
}) {
  const FeaturedIcon = menu.featured.icon;

  return (
    <div className="fixed inset-x-0 top-[76px] z-[100] w-screen">
      <div
        className={[
          'w-full border-b shadow-[0_24px_70px_rgba(9,30,66,.14)]',
          variant === 'dark' ? 'border-white/10 bg-[#06162C]' : 'border-slate-200 bg-white',
        ].join(' ')}
      >
        <div className="h-1 w-full bg-gradient-to-r from-[#FF6A00] via-orange-300 to-transparent" />

        <div className="mx-auto grid w-full max-w-[1440px] lg:grid-cols-[320px_minmax(0,1fr)]">
          <div className="min-h-[420px] bg-[#06162C] p-7 text-white lg:p-8">
            <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-orange-500/15 text-orange-300 ring-1 ring-orange-300/15">
              <FeaturedIcon size={21} />
            </div>

            <div className="mt-6 text-[10px] font-bold uppercase tracking-[0.18em] text-orange-300">
              {menu.featured.eyebrow}
            </div>

            <h3 className="mt-3 max-w-[250px] text-[25px] font-bold leading-[1.15] tracking-[-0.035em]">
              {menu.featured.title}
            </h3>

            <p className="mt-3 max-w-[265px] text-xs leading-6 text-white/65">
              {menu.featured.description}
            </p>

            <Link
              href={menu.featured.href}
              onClick={onNavigate}
              className="group mt-6 inline-flex items-center gap-2 rounded-full bg-white px-4 py-2.5 text-xs font-bold text-[#0A1C33] transition-colors hover:bg-orange-50"
            >
              Explore {menu.label}
              <ArrowRight size={13} className="transition-transform group-hover:translate-x-0.5" />
            </Link>

            <div className="mt-8 grid grid-cols-2 gap-2">
              {[
                ['Connected', 'Platform'],
                ['Scalable', 'Workflows'],
                ['Secure', 'Access'],
                ['Actionable', 'Insights'],
              ].map(([a, b]) => (
                <div
                  key={`${a}-${b}`}
                  className="border border-white/10 bg-white/[0.04] px-3 py-2.5"
                >
                  <div className="text-[9px] font-semibold text-white">{a}</div>
                  <div className="mt-0.5 text-[8px] text-white/65">{b}</div>
                </div>
              ))}
            </div>
          </div>

          <div
            className={[
              'min-w-0 p-4 sm:p-5 lg:p-6',
              variant === 'dark' ? 'bg-[#06162C]' : 'bg-white',
            ].join(' ')}
          >
            <div className="mb-4 flex items-center justify-between px-1">
              <div>
                <div
                  className={[
                    'text-sm font-bold',
                    variant === 'dark' ? 'text-white' : 'text-[#0A1C33]',
                  ].join(' ')}
                >
                  Explore {menu.label}
                </div>
                <div
                  className={[
                    'mt-0.5 text-[10px]',
                    variant === 'dark' ? 'text-white/65' : 'text-slate-600',
                  ].join(' ')}
                >
                  Explore the capabilities available in Mentrox OneHR.
                </div>
              </div>

              <Link
                href={menu.href}
                onClick={onNavigate}
                className="hidden items-center gap-1 text-[11px] font-bold text-orange-500 sm:inline-flex"
              >
                View all
                <ArrowRight size={12} />
              </Link>
            </div>

            <div className="grid grid-cols-1 gap-1 sm:grid-cols-2">
              {menu.items.map((item) => {
                const Icon = item.icon;

                return (
                  <Link
                    key={item.title}
                    href={item.href}
                    onClick={onNavigate}
                    className={[
                      'group flex min-h-[78px] min-w-0 items-center gap-3 border px-3 py-3 transition-all duration-200',
                      variant === 'dark'
                        ? 'border-transparent hover:border-white/10 hover:bg-white/[0.04]'
                        : 'border-transparent hover:border-slate-200 hover:bg-slate-50',
                    ].join(' ')}
                  >
                    <div
                      className={[
                        'flex h-10 w-10 shrink-0 items-center justify-center rounded-xl transition-colors',
                        variant === 'dark'
                          ? 'bg-white/5 text-white/55 group-hover:bg-orange-500/10 group-hover:text-orange-300'
                          : 'bg-slate-50 text-slate-500 group-hover:bg-orange-50 group-hover:text-orange-600',
                      ].join(' ')}
                    >
                      <Icon size={17} strokeWidth={1.9} />
                    </div>

                    <div className="min-w-0 flex-1">
                      <div className="flex items-center justify-between gap-2">
                        <div
                          className={[
                            'truncate text-xs font-bold',
                            variant === 'dark' ? 'text-white' : 'text-[#0A1C33]',
                          ].join(' ')}
                        >
                          {item.title}
                        </div>

                        <ArrowRight
                          size={12}
                          className={[
                            'shrink-0 transition-all group-hover:translate-x-0.5',
                            variant === 'dark'
                              ? 'text-white/15 group-hover:text-orange-300'
                              : 'text-slate-200 group-hover:text-orange-500',
                          ].join(' ')}
                        />
                      </div>

                      <p
                        className={[
                          'mt-1 line-clamp-2 text-[10px] leading-4',
                          variant === 'dark' ? 'text-white/65' : 'text-slate-600',
                        ].join(' ')}
                      >
                        {item.description}
                      </p>
                    </div>
                  </Link>
                );
              })}
            </div>

            <div
              className={[
                'mt-4 flex items-center justify-between border px-4 py-3',
                variant === 'dark'
                  ? 'border-orange-400/10 bg-orange-500/[0.06]'
                  : 'border-orange-100 bg-orange-50/60',
              ].join(' ')}
            >
              <div className="min-w-0">
                <div
                  className={[
                    'text-[10px] font-bold',
                    variant === 'dark' ? 'text-white' : 'text-[#0A1C33]',
                  ].join(' ')}
                >
                  Need help choosing?
                </div>
                <div
                  className={[
                    'mt-0.5 truncate text-[9px]',
                    variant === 'dark' ? 'text-white/65' : 'text-slate-500',
                  ].join(' ')}
                >
                  Talk to our team about your workforce requirements.
                </div>
              </div>

              <Link
                href="/request-demo"
                onClick={onNavigate}
                className="ml-4 inline-flex shrink-0 items-center gap-1 rounded-full bg-[#c94e00] px-3 py-2 text-[10px] font-bold text-white"
              >
                Request Demo
                <ArrowRight size={11} />
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function MarketingHeader({
  variant = 'light',
  searchIndex,
}: {
  variant?: HeaderVariant;
  searchIndex: readonly PublicSearchEntry[];
}) {
  const [openMenu, setOpenMenu] = useState<string | null>(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const closeAll = () => {
    setOpenMenu(null);
    setMobileOpen(false);
  };

  const dark = variant === 'dark';

  return (
    <>
      <header
        className={[
          'z-[90] w-full',
          dark
            ? 'absolute inset-x-0 top-0'
            : 'sticky top-0 border-b border-slate-200/80 bg-white/95 backdrop-blur-xl',
        ].join(' ')}
        onMouseLeave={() => setOpenMenu(null)}
      >
        <div className="mx-auto max-w-[1500px] px-4 sm:px-5 lg:px-8">
          <div
            className={[
              'flex h-[76px] items-center justify-between',
              dark
                ? 'rounded-b-2xl border-x border-b border-white/10 bg-[#071A31]/80 px-4 backdrop-blur-xl lg:px-5'
                : 'px-0',
            ].join(' ')}
          >
            <Logo variant={variant} />

            <nav className="hidden items-center gap-0.5 xl:flex">
              {menus.map((menu) => {
                const active = openMenu === menu.label;

                return (
                  <div
                    key={menu.label}
                    className="static"
                    onMouseEnter={() => setOpenMenu(menu.label)}
                  >
                    <Link
                      href={menu.href}
                      className={[
                        'flex items-center gap-1 rounded-xl px-4 py-2.5 text-sm font-medium transition-all',
                        dark
                          ? active
                            ? 'bg-white/10 text-white'
                            : 'text-white/65 hover:bg-white/5 hover:text-white'
                          : active
                            ? 'bg-slate-50 text-[#0A1C33]'
                            : 'text-slate-600 hover:bg-slate-50 hover:text-[#0A1C33]',
                      ].join(' ')}
                    >
                      {menu.label}
                      <ChevronDown
                        size={13}
                        className={[
                          'transition-transform duration-200',
                          active ? 'rotate-180' : '',
                        ].join(' ')}
                      />
                    </Link>

                    {active ? (
                      <MegaMenuDropdown menu={menu} variant={variant} onNavigate={closeAll} />
                    ) : null}
                  </div>
                );
              })}

              <Link
                href="/pricing"
                className={[
                  'rounded-xl px-4 py-2.5 text-sm font-medium transition-colors',
                  dark
                    ? 'text-white/65 hover:bg-white/5 hover:text-white'
                    : 'text-slate-600 hover:bg-slate-50 hover:text-[#0A1C33]',
                ].join(' ')}
              >
                Pricing
              </Link>

              <Link
                href="/resources"
                className={[
                  'rounded-xl px-4 py-2.5 text-sm font-medium transition-colors',
                  dark
                    ? 'text-white/65 hover:bg-white/5 hover:text-white'
                    : 'text-slate-600 hover:bg-slate-50 hover:text-[#0A1C33]',
                ].join(' ')}
              >
                Resources
              </Link>

              <Link
                href="/about"
                className={[
                  'rounded-xl px-4 py-2.5 text-sm font-medium transition-colors',
                  dark
                    ? 'text-white/65 hover:bg-white/5 hover:text-white'
                    : 'text-slate-600 hover:bg-slate-50 hover:text-[#0A1C33]',
                ].join(' ')}
              >
                Company
              </Link>
            </nav>

            <div className="ml-auto flex items-center gap-2 xl:ml-0">
              <PublicSearch
                index={searchIndex}
                variant={variant}
                onOpen={() => {
                  setOpenMenu(null);
                  setMobileOpen(false);
                }}
              />

              <Link
                href="/sign-up-login-screen"
                className={[
                  'hidden rounded-xl px-4 py-2.5 text-sm font-semibold transition-colors xl:inline-flex',
                  dark
                    ? 'border border-white/15 text-white/85 hover:bg-white/5 hover:text-white'
                    : 'border border-slate-200 text-slate-700 hover:bg-slate-50',
                ].join(' ')}
              >
                Sign In
              </Link>

              <Link
                href="/request-demo"
                className="hidden rounded-xl bg-[#c94e00] xl:inline-flex px-5 py-2.5 text-sm font-bold text-white shadow-[0_12px_30px_rgba(255,106,0,.18)] transition-all hover:-translate-y-0.5 hover:bg-orange-800"
              >
                Request a Demo
              </Link>
            </div>

            <button
              type="button"
              aria-label={mobileOpen ? 'Close navigation' : 'Open navigation'}
              aria-expanded={mobileOpen}
              onClick={() => {
                setMobileOpen((value) => !value);
                setOpenMenu(null);
              }}
              className={[
                'ml-2 flex h-10 w-10 items-center justify-center rounded-xl border xl:hidden',
                dark ? 'border-white/10 bg-white/5 text-white' : 'border-slate-200 text-slate-700',
              ].join(' ')}
            >
              {mobileOpen ? <X size={19} /> : <Menu size={19} />}
            </button>
          </div>

          {mobileOpen ? (
            <div
              className={[
                'mt-2 p-3 shadow-xl xl:hidden',
                dark
                  ? 'rounded-2xl border border-white/10 bg-[#0B2342]'
                  : 'rounded-2xl border border-slate-200 bg-white',
              ].join(' ')}
            >
              {[
                ['Platform', '/platform'],
                ['Solutions', '/solutions'],
                ['Industries', '/industries'],
                ['Pricing', '/pricing'],
                ['Resources', '/resources'],
                ['Company', '/about'],
              ].map(([label, href]) => (
                <Link
                  key={label}
                  href={href}
                  onClick={closeAll}
                  className={[
                    'flex items-center justify-between rounded-xl px-3 py-3 text-sm',
                    dark
                      ? 'text-white/70 hover:bg-white/5 hover:text-white'
                      : 'text-slate-600 hover:bg-slate-50 hover:text-[#0A1C33]',
                  ].join(' ')}
                >
                  {label}
                  <ArrowRight size={15} />
                </Link>
              ))}

              <div className={['my-2 h-px', dark ? 'bg-white/10' : 'bg-slate-100'].join(' ')} />

              <Link
                href="/sign-up-login-screen"
                onClick={closeAll}
                className={[
                  'flex h-11 items-center justify-center rounded-xl text-sm font-semibold',
                  dark
                    ? 'border border-white/10 text-white'
                    : 'border border-slate-200 text-slate-700',
                ].join(' ')}
              >
                Sign In
              </Link>

              <Link
                href="/request-demo"
                onClick={closeAll}
                className="mt-2 flex h-11 items-center justify-center rounded-xl bg-[#c94e00] text-sm font-semibold text-white"
              >
                Request a Demo
              </Link>
            </div>
          ) : null}
        </div>
      </header>
    </>
  );
}
